import React from 'react';

import { AlertCircle, Clock } from 'lucide-react';
import PropTypes from 'prop-types';

import ColorButton from '@/components/common/elements/ColorButton';
import classes from '@/components/ListBackup/style.module.scss';

const formatDateTime = dateString => {
  if (!dateString) return '';

  const [datePart, timePart] = dateString.split('_');
  if (!datePart || !timePart) return dateString;

  const year = datePart.slice(0, 4);
  const month = datePart.slice(4, 6);
  const day = datePart.slice(6, 8);

  const hour = timePart.slice(0, 2);
  const minute = timePart.slice(2, 4);

  return `${day}/${month}/${year} - ${hour}:${minute}`;
};

const ListBackupComponent = ({
  error,
  handleSubmit,
  handleClose,
  handleRequestBackup,
  lastBackupUpdate,
  intl,
  loading,
  requestingBackup,
  submitting,
}) => {
  const isBusy = submitting || requestingBackup;

  return (
    <div className={classes.container} style={{ whiteSpace: 'pre-line' }}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {intl.formatMessage({ id: 'loading' })}...
        </div>
      ) : (
        <>
          {error && (
            <div style={{ color: '#FF3B30', fontSize: '13px', marginBottom: '12px', fontWeight: 600 }}>
              {error}
            </div>
          )}

          <div style={{ color: '#6B6F77', fontSize: '16px', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
            <AlertCircle style={{ fontSize: '20px', marginRight: '4px', color: lastBackupUpdate ? '#00BBDD' : '#FF3B30' }} />
            {intl.formatMessage({ id: lastBackupUpdate ? 'list_backups_title' : 'list_backups_no_backups_title' })}
          </div>

          <div style={{ marginTop: '14px', fontSize: '12px', fontWeight: 400 }}>
            {intl.formatMessage(
              { id: lastBackupUpdate ? 'list_backups_description' : 'list_backups_no_backups_description' },
              {
                b: chunks => <strong>{chunks}</strong>,
              }
            )}
          </div>

          {lastBackupUpdate && (
            <>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div
                  style={{
                    marginTop: '14px',
                    fontSize: '12px',
                    fontWeight: 400,
                    backgroundColor: '#00BBDD1A',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Clock style={{ fontSize: '16px', marginRight: '4px', color: '#000000' }} />
                  <div style={{ paddingTop: '2px' }}>
                    <b style={{ marginRight: '4px' }}>{intl.formatMessage({ id: 'list_backups_last_update' })}</b>{' '}
                    {formatDateTime(lastBackupUpdate.datetime)}
                  </div>
                </div>
              </div>

              <div
                style={{
                  whiteSpace: 'pre-line',
                  backgroundColor: '#FDBC520D',
                  border: '1px solid #FDBC52',
                  borderRadius: '4px',
                  marginTop: '14px',
                  fontSize: '12px',
                  fontWeight: 400,
                  padding: '8px',
                }}
              >
                {intl.formatMessage(
                  { id: 'list_backups_warning' },
                  {
                    b: chunks => <strong>{chunks}</strong>,
                  }
                )}
              </div>
            </>
          )}

          <div
            className={`${classes.field} ${classes.actions}`}
            style={{ marginTop: '32px', display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}
          >
            <ColorButton
              type="button"
              color="blue"
              onClick={handleRequestBackup}
              disabled={isBusy}
            >
              {requestingBackup
                ? intl.formatMessage({ id: 'list_backups_requesting', defaultMessage: 'Solicitando...' })
                : intl.formatMessage({ id: 'list_backups_request', defaultMessage: 'Solicitar backup do dispositivo' })}
            </ColorButton>

            {lastBackupUpdate ? (
              <>
                <ColorButton type="button" color="red" onClick={handleClose} disabled={isBusy}>
                  {intl.formatMessage({ id: 'CANCEL' })}
                </ColorButton>

                <ColorButton type="submit" color="green" onClick={handleSubmit} disabled={isBusy}>
                  {intl.formatMessage({ id: 'list_backups_download' })}
                </ColorButton>
              </>
            ) : (
              <ColorButton type="button" color="gray" onClick={handleClose} disabled={isBusy}>
                {intl.formatMessage({ id: 'CLOSE' })}
              </ColorButton>
            )}
          </div>
        </>
      )}
    </div>
  );
};

ListBackupComponent.propTypes = {
  error: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleRequestBackup: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  requestingBackup: PropTypes.bool,
  submitting: PropTypes.bool.isRequired,
  lastBackupUpdate: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
};

ListBackupComponent.defaultProps = {
  error: null,
  requestingBackup: false,
  lastBackupUpdate: null,
};

export default ListBackupComponent;
